/**
 * LinguistPro — every sentence the payment modal shows, in one place.
 *
 * Copy is Guru signal 975d8d72 (CEO-approved 21 Sep). Where the backend makes
 * a Guru sentence untrue it is corrected and the line carries a
 * `// deviation from 975d8d72: <reason>` comment. Pure strings and functions:
 * no React, no DOM.
 */

import { ONSITE_MESSAGES, type PaymentPlan } from './onsitePayment';

/** The support address the app already publishes (Terms, Privacy, Organisation). */
export const SUPPORT_EMAIL = 'info@techsight.co.za';
export const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}`;

export const BEFORE =
  "Topping up your LinguistPro balance. You'll be taken to PayFast to complete payment securely. Nothing is charged until you confirm there. Your balance updates within seconds of payment clearing.";

// deviation from 975d8d72: a gift card credits a redemption code in the Wallet, not the buyer's balance, so the balance sentences would be untrue
export const BEFORE_GIFT =
  "Buying a LinguistPro gift card. You'll be taken to PayFast to complete payment securely. Nothing is charged until you confirm there. Your gift card code appears in your Wallet once payment has cleared.";

// deviation from 975d8d72: the balance is polled for up to two minutes, so "30 seconds" would be untrue
export const PROCESSING =
  "Confirming your payment. This can take up to 2 minutes, please don't close this window.";

/** Only where nothing was ever sent to PayFast, or PayFast itself sent the customer back cancelled. */
const NOTHING_TAKEN = 'No amount was taken from your account.';

// deviation from 975d8d72: the "[Use a different payment method]" button is omitted because the method is chosen inside PayFast's own window, so it would duplicate Try again
export const FAILED = `Payment didn't go through. ${NOTHING_TAKEN}`;

/**
 * When the outcome is unknown — PayFast's callback(false), a closed or silent
 * window, our own timeout — money may still move (EFT-type methods settle
 * later), so the wording is Guru ruling 5b8a421b's, owned by onsitePayment.ts.
 */
export function unconfirmedMessage(plan: PaymentPlan | null, kind: 'cancelled' | 'incomplete'): string {
  return ONSITE_MESSAGES[plan ?? 'payg'][kind];
}

export const PREVIOUS_ATTEMPT_UNFINISHED =
  "Your previous payment attempt didn't finish. If it goes through, your balance will show it; check your balance before paying again.";

/** A superseded attempt's real paid callback arrived while this attempt's PayFast window was already open. */
export const EARLIER_PAYMENT_ALSO_PAID =
  'Your earlier payment also went through. If you were charged twice, contact support and we will refund the duplicate.';

export const SIGN_IN_AGAIN = 'Please sign in again.';

/** The balance rose, but by less than this payment's pack — never claim the pack amount. */
export const BALANCE_UPDATED = 'Payment received. Your balance has been updated.';

export const CREDIT_PENDING =
  "Payment received. Your balance will update shortly. If it hasn't within 10 minutes, contact support.";

export const ERROR_FALLBACK = `Something went wrong starting the payment. ${NOTHING_TAKEN}`;

// deviation from 975d8d72: the Guru line also promised "A receipt and VAT invoice are on their way to [email]"; the app sends no VAT invoice and the confirmation email depends on notification preferences, so the sentence is omitted
export function successMessage(credit: number | null, plan: PaymentPlan = 'payg'): string {
  if (plan === 'gift') {
    return 'Payment received. Your gift card code will appear in your Wallet → Gift Cards shortly.';
  }
  if (credit === null) {
    // deviation from 975d8d72: the PayFast return URL carries no pack, so the credit amount is unknown on that path
    return 'Payment received. Your credit is being added to your balance.';
  }
  return `Payment received. R${credit} of credit has been added to your balance.`;
}

export function beforeMessage(plan: PaymentPlan | null): string {
  return plan === 'gift' ? BEFORE_GIFT : BEFORE;
}

export function rateLimitedMessage(retryAfterSeconds: number | null): string {
  if (retryAfterSeconds === null || !Number.isFinite(retryAfterSeconds)) {
    return 'Too many payment attempts. Please wait a few minutes and try again.';
  }
  const minutes = Math.max(1, Math.ceil(retryAfterSeconds / 60));
  return `Too many payment attempts. Please wait ${minutes} ${minutes === 1 ? 'minute' : 'minutes'} and try again.`;
}

export function errorMessage(serverMessage: string | null): string {
  return serverMessage && serverMessage.trim() ? serverMessage : ERROR_FALLBACK;
}

export const ACTION_LABELS = {
  retry: 'Try again',
  contact_support: 'Contact support',
  close: 'Close',
} as const;
